import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/components/common/Button/Button';
import { historyError, salesHistoryApi } from './api';
import type {
  SalesComparedDeal, SalesDealChangeField, SalesDealDifference, SalesPeriod, SalesPlanComparison,
} from './types';
import { formatDate, money } from './utils';
import css from './PlanHistory.module.css';

const kindLabel = {
  closed: 'Закрытый факт',
  preparation: 'К отгрузке',
  planned: 'Не участвует в зачёте',
};

const changeLabel: Record<SalesDealChangeField, string> = {
  kind: 'статус зачёта', date: 'дата', employee: 'ответственный', stage_id: 'этап',
  opportunity: 'оборот', profit: 'прибыль', actual_paid: 'оплачено',
};

function delta(value: number) {
  return `${value > 0 ? '+' : ''}${money(value)}`;
}

function value(row: SalesComparedDeal | null, field: 'opportunity' | 'profit' | 'actual_paid') {
  if (!row?.countsTowardPlan || (field === 'actual_paid' && row.deal.kind !== 'closed')) return 0;
  return Number(row.deal[field]);
}

function creditLabel(row: SalesComparedDeal | null, empty: string) {
  if (!row) return empty;
  const label = `${kindLabel[row.deal.kind]} · ${row.deal.stage_id || 'этап не указан'}`;
  return row.countsTowardPlan ? label : `${label} (вне выбранного периода/отдела)`;
}

function displayedValue(row: SalesComparedDeal | null, field: 'opportunity' | 'profit' | 'actual_paid') {
  if (!row) return '—';
  const raw = Number(row.deal[field]);
  const contribution = value(row, field);
  return contribution === raw ? money(raw) : `${money(raw)} (в зачёте ${money(contribution)})`;
}

function dealStatus(change: SalesDealDifference) {
  if (change.status === 'added') return 'Добавлена в зачёт';
  if (change.status === 'removed') return 'Исключена из зачёта';
  return 'Изменена';
}

function cellPair(before: string, current: string) {
  return <><span>{before}</span><span className={css.arrow}>→</span><span>{current}</span></>;
}

function DealDifferenceRow({ change }: { change: SalesDealDifference }) {
  const before = change.snapshot;
  const current = change.current;
  const identifying = current || before!;
  const amount = (field: 'opportunity' | 'profit' | 'actual_paid') => {
    const oldValue = value(before, field);
    const newValue = value(current, field);
    return cellPair(displayedValue(before, field), `${displayedValue(current, field)} (разница зачёта ${delta(newValue - oldValue)})`);
  };
  return <tr>
    <td><span className={`${css.changeBadge} ${css[change.status]}`}>{dealStatus(change)}</span></td>
    <td><a href={`https://crm.okgnn.ru/crm/deal/details/${identifying.deal.id}/`} target="_blank" rel="noopener noreferrer">
      Сделка №{identifying.deal.id}
    </a><br/>{identifying.deal.client || 'Без названия'}{identifying.deal.inn ? ` · ИНН ${identifying.deal.inn}` : ''}</td>
    <td>{cellPair(creditLabel(before, 'Не было'), creditLabel(current, 'Удалена из системы'))}</td>
    <td>{cellPair(before?.employeeName || '—', current?.employeeName || '—')}</td>
    <td>{cellPair(before ? formatDate(before.deal.date) : '—', current ? formatDate(current.deal.date) : '—')}</td>
    <td>{amount('opportunity')}</td>
    <td>{amount('profit')}</td>
    <td>{amount('actual_paid')}</td>
    <td>{change.changedFields.length ? change.changedFields.map(field => changeLabel[field]).join(', ') : 'состав зачёта'}</td>
  </tr>;
}

export function SnapshotComparisonResult({ comparison }: { comparison: SalesPlanComparison }) {
  const summary = [
    ['Закрытый факт — оборот', comparison.opportunity.snapshot.closed, comparison.opportunity.current.closed, comparison.opportunity.delta.closed],
    ['К отгрузке — оборот', comparison.opportunity.snapshot.preparation, comparison.opportunity.current.preparation, comparison.opportunity.delta.preparation],
    ['Итого в зачёте — оборот', comparison.opportunity.snapshot.total, comparison.opportunity.current.total, comparison.opportunity.delta.total],
    ['Закрытый факт — прибыль', comparison.profit.snapshot.closed, comparison.profit.current.closed, comparison.profit.delta.closed],
    ['К отгрузке — прибыль', comparison.profit.snapshot.preparation, comparison.profit.current.preparation, comparison.profit.delta.preparation],
    ['Итого в зачёте — прибыль', comparison.profit.snapshot.total, comparison.profit.current.total, comparison.profit.delta.total],
    ['Оплачено по закрытым (справочно)', comparison.paid.snapshot, comparison.paid.current, comparison.paid.delta],
  ] as const;

  return <>
    <p className={comparison.hasDifferences ? css.warningNotice : css.successNotice} role="status">
      {comparison.hasDifferences
        ? `Обнаружены расхождения: ${comparison.deals.length} сделок требуют внимания.`
        : 'Расхождений нет: показатели, участвующие в зачёте плана, совпадают со срезом.'}
      {' '}Проверено {new Date(comparison.comparedAt).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })} (МСК).
    </p>
    <div className={css.tableWrap}><table aria-label="Изменение показателей зачёта">
      <caption>Изменение показателей зачёта</caption>
      <thead><tr><th>Показатель</th><th>В срезе</th><th>Сейчас</th><th>Разница</th></tr></thead>
      <tbody>{summary.map(([label, before, current, difference]) => <tr key={label}>
        <th>{label}</th><td>{money(before)}</td><td>{money(current)}</td>
        <td className={difference ? css.changedValue : undefined}>{delta(difference)}</td>
      </tr>)}</tbody>
    </table></div>
    {comparison.deals.length > 0 && <div className={css.tableWrap}><table aria-label="Расхождения по сделкам">
      <caption>Расхождения по сделкам</caption>
      <thead><tr><th>Изменение</th><th>Сделка</th><th>Зачёт</th><th>Ответственный</th><th>Дата</th><th>Оборот</th><th>Прибыль</th><th>Оплачено</th><th>Изменённые поля</th></tr></thead>
      <tbody>{comparison.deals.map(change => <DealDifferenceRow key={change.key} change={change}/>)}</tbody>
    </table></div>}
  </>;
}

export function SnapshotComparison({ period }: { period: SalesPeriod }) {
  const [comparison, setComparison] = useState<SalesPlanComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const load = async () => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setLoading(true); setError(''); setComparison(null);
    try {
      const result = await salesHistoryApi.comparison(period, request.signal);
      if (!request.signal.aborted) setComparison(result);
    } catch (err) {
      if (!request.signal.aborted) setError(historyError(err));
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  };

  return <section className={css.section}>
    <div className={css.heading}>
      <div><h3>Срез и текущая система</h3>
        <p>Проверка показывает только сделки, влияющие на выполнение: закрытый факт и «к отгрузке».</p></div>
      <Button variant="secondary" disabled={loading} onClick={() => void load()}>
        {comparison ? 'Обновить сравнение' : 'Сравнить с текущей системой'}
      </Button>
    </div>
    {loading && <p role="status">Считываем текущие сделки…</p>}
    {error && <p role="alert" className={css.error}>{error}</p>}
    {comparison && <SnapshotComparisonResult comparison={comparison}/>} 
  </section>;
}
